import classnames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { buildSkillTreeEffects, type SkillTreeEffectsOptions } from './skill-tree-effects';
import { SkillTreeEffectsRenderer } from './skill-tree-effects-renderer';
import type { SkillTreeModel } from './skill-tree-model';
import type { SkillTreeViewStore } from './skill-tree-view';
import { useCanvasFrameLoop } from './use-canvas-frame-loop';
import styles from './SkillTree.module.scss';

interface SkillTreeEffectsLayerProps {
    readonly model: SkillTreeModel;
    /** The tree's pan/zoom; the effects are drawn with exactly the same mapping, read each frame. */
    readonly viewStore: SkillTreeViewStore;
    /**
     * Animate and show the effects. Off when the tree is hidden, when
     * "Character Home effects" is off, or when the OS asks for reduced
     * motion; the SVG pieces' own static glows stand in then.
     */
    readonly isActive: boolean;
    /** Intro timing and bursts (see `useSkillTreeIntro`); each change restarts the effects' clock. */
    readonly timing: SkillTreeEffectsOptions;
}

/**
 * The skill tree's effects layer: a transparent 2D canvas over the tree's
 * SVG that draws its animated light (`SkillTreeEffectsRenderer`), screened
 * onto the page so it only ever brightens what is under it. It draws
 * nothing interactive, so pointer events pass through.
 */
export function SkillTreeEffectsLayer({ model, viewStore, isActive, timing }: SkillTreeEffectsLayerProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderer = useMemo(() => new SkillTreeEffectsRenderer(), []);

    useEffect(() => {
        renderer.setEffects(buildSkillTreeEffects(model, timing));
    }, [renderer, model, timing]);

    // While active, the light animates every frame, following the latest view.
    useCanvasFrameLoop(containerRef, isActive, (deltaSeconds, viewport) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        renderer.update(deltaSeconds);
        renderer.draw(canvas, viewStore.get(), viewport);
    });

    return (
        <div ref={containerRef} className={classnames(styles.effectsLayer, isActive && styles.visible)} aria-hidden="true">
            <canvas ref={canvasRef} />
        </div>
    );
}
